// ============================================================
// API-Hockey (api-sports.io) Client
// Base URL: https://v1.hockey.api-sports.io
// Auth: x-apisports-key header
// NHL League ID: 57  Season: 2024
// ============================================================

const BASE_URL = 'https://v1.hockey.api-sports.io';
const NHL_LEAGUE_ID = 57;
const CURRENT_SEASON = 2024;

function getHeaders(): HeadersInit {
  const key = process.env.NHL_API_KEY;
  if (!key || key === 'your-nhl-api-key-here') {
    throw new Error('NHL_API_KEY is not configured');
  }
  return {
    'x-apisports-key': key,
    'Content-Type': 'application/json',
  };
}

async function apiFetch<T>(path: string, params?: Record<string, string | number>): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, String(v)));
  }

  const res = await fetch(url.toString(), {
    headers: getHeaders(),
    next: { revalidate: 30 },
  });

  if (!res.ok) {
    throw new Error(`API-Hockey error ${res.status}: ${res.statusText}`);
  }

  const json = await res.json();
  if (json.errors && Object.keys(json.errors).length > 0) {
    throw new Error(`API-Hockey API error: ${JSON.stringify(json.errors)}`);
  }

  return json as T;
}

// ============================================================
// TYPES
// ============================================================

export interface ApiHockeyGame {
  id: number;
  date: string;
  time: string;
  timestamp: number;
  timezone: string;
  week: string | null;
  venue: string | null;
  status: {
    long: string;
    short: string;
    timer: string | null;
  };
  league: {
    id: number;
    name: string;
    type: string;
    logo: string;
    season: number;
  };
  country: {
    id: number;
    name: string;
    code: string;
    flag: string;
  };
  teams: {
    home: { id: number; name: string; logo: string };
    away: { id: number; name: string; logo: string };
  };
  scores: {
    home: { period1: number | null; period2: number | null; period3: number | null; overtime: number | null; total: number | null };
    away: { period1: number | null; period2: number | null; period3: number | null; overtime: number | null; total: number | null };
  };
  periods: {
    first: number | null;
    second: number | null;
    third: number | null;
    overtime: number | null;
  };
  events: ApiHockeyEvent[];
}

export interface ApiHockeyEvent {
  id: number;
  period: { current: number; total: number };
  team: { id: number; name: string; logo: string };
  player: { id: number; name: string };
  assist: { id: number | null; name: string | null };
  type: string;
  detail: string;
  minute: number;
  second: number | null;
  comment: string | null;
}

export interface ApiHockeyTeam {
  id: number;
  name: string;
  code: string | null;
  city: string | null;
  logo: string;
  national: boolean;
  country: { id: number; name: string; code: string; flag: string };
}

export interface ApiHockeyStanding {
  position: number;
  stage: string;
  group: { id: number | null; name: string | null };
  team: { id: number; name: string; logo: string };
  league: { id: number; name: string; logo: string; season: number };
  country: { id: number; name: string; code: string; flag: string };
  games: { played: number; win: { total: number; percentage: string }; lose: { total: number; percentage: string }; draw: number };
  goals: { for: number; against: number };
  points: number;
  description: string | null;
}

export interface ApiHockeyResponse<T> {
  get: string;
  parameters: Record<string, string>;
  errors: Record<string, string>;
  results: number;
  paging: { current: number; total: number };
  response: T[];
}

// ============================================================
// ENDPOINTS
// ============================================================

/** Get today's NHL games */
export async function getNHLGamesToday(): Promise<ApiHockeyGame[]> {
  const today = new Date().toISOString().split('T')[0];
  const data = await apiFetch<ApiHockeyResponse<ApiHockeyGame>>('/games', {
    league: NHL_LEAGUE_ID,
    season: CURRENT_SEASON,
    date: today,
  });
  return data.response || [];
}

/** Get live NHL games (status: LIVE) */
export async function getNHLLiveGames(): Promise<ApiHockeyGame[]> {
  const data = await apiFetch<ApiHockeyResponse<ApiHockeyGame>>('/games', {
    league: NHL_LEAGUE_ID,
    season: CURRENT_SEASON,
    live: 'all',
  });
  return data.response || [];
}

/** Get NHL standings */
export async function getNHLStandings(): Promise<ApiHockeyStanding[]> {
  const data = await apiFetch<ApiHockeyResponse<ApiHockeyStanding[]>>('/standings', {
    league: NHL_LEAGUE_ID,
    season: CURRENT_SEASON,
  });
  // standings response is nested array
  return (data.response || []).flat();
}

/** Get NHL teams */
export async function getNHLTeams(): Promise<ApiHockeyTeam[]> {
  const data = await apiFetch<ApiHockeyResponse<ApiHockeyTeam>>('/teams', {
    league: NHL_LEAGUE_ID,
    season: CURRENT_SEASON,
  });
  return data.response || [];
}

/** Get game events (goals, penalties) */
export async function getGameEvents(gameId: number): Promise<ApiHockeyEvent[]> {
  const data = await apiFetch<ApiHockeyResponse<ApiHockeyEvent>>('/games/events', {
    game: gameId,
  });
  return data.response || [];
}

/** Get games by date range for historical data */
export async function getNHLGamesByDate(date: string): Promise<ApiHockeyGame[]> {
  const data = await apiFetch<ApiHockeyResponse<ApiHockeyGame>>('/games', {
    league: NHL_LEAGUE_ID,
    season: CURRENT_SEASON,
    date,
  });
  return data.response || [];
}

/** Get API status / account info */
export async function getApiStatus(): Promise<{ account: { firstname: string; lastname: string; email: string }; subscription: { plan: string; end: string; active: boolean }; requests: { current: number; limit_day: number } }> {
  const res = await fetch(`${BASE_URL}/status`, {
    headers: getHeaders(),
  });
  const json = await res.json();
  return json.response;
}

// ============================================================
// HELPERS — map API-Hockey data to app types
// ============================================================

export function mapGameStatus(shortStatus: string): 'live' | 'scheduled' | 'final' {
  if (['1P', '2P', '3P', 'OT', 'BT', 'LIVE', 'HT', 'ET', 'PT', 'BREAK'].includes(shortStatus)) return 'live';
  if (['FT', 'AOT', 'AP'].includes(shortStatus)) return 'final';
  return 'scheduled';
}

export function getTeamAbbr(name: string): string {
  const map: Record<string, string> = {
    'Anaheim Ducks': 'ANA', 'Arizona Coyotes': 'ARI', 'Boston Bruins': 'BOS',
    'Buffalo Sabres': 'BUF', 'Calgary Flames': 'CGY', 'Carolina Hurricanes': 'CAR',
    'Chicago Blackhawks': 'CHI', 'Colorado Avalanche': 'COL', 'Columbus Blue Jackets': 'CBJ',
    'Dallas Stars': 'DAL', 'Detroit Red Wings': 'DET', 'Edmonton Oilers': 'EDM',
    'Florida Panthers': 'FLA', 'Los Angeles Kings': 'LAK', 'Minnesota Wild': 'MIN',
    'Montréal Canadiens': 'MTL', 'Montreal Canadiens': 'MTL', 'Nashville Predators': 'NSH',
    'New Jersey Devils': 'NJD', 'New York Islanders': 'NYI', 'New York Rangers': 'NYR',
    'Ottawa Senators': 'OTT', 'Philadelphia Flyers': 'PHI', 'Pittsburgh Penguins': 'PIT',
    'San Jose Sharks': 'SJS', 'Seattle Kraken': 'SEA', 'St. Louis Blues': 'STL',
    'Tampa Bay Lightning': 'TBL', 'Toronto Maple Leafs': 'TOR', 'Utah Hockey Club': 'UTA',
    'Vancouver Canucks': 'VAN', 'Vegas Golden Knights': 'VGK', 'Washington Capitals': 'WSH',
    'Winnipeg Jets': 'WPG',
  };
  return map[name] || name.substring(0, 3).toUpperCase();
}

export function getTeamColor(abbr: string): string {
  const colors: Record<string, string> = {
    ANA: '#F47A38', ARI: '#8C2633', BOS: '#FCB514', BUF: '#003087',
    CGY: '#CE1126', CAR: '#CC0000', CHI: '#CF0A2C', COL: '#6F263D',
    CBJ: '#002654', DAL: '#006847', DET: '#CE1126', EDM: '#FF4C00',
    FLA: '#C8102E', LAK: '#A2AAAD', MIN: '#154734', MTL: '#AF1E2D',
    NSH: '#FFB81C', NJD: '#CE1126', NYI: '#00539B', NYR: '#0038A8',
    OTT: '#C2912C', PHI: '#F74902', PIT: '#FCB514', SJS: '#006D75',
    SEA: '#001628', STL: '#002F87', TBL: '#002868', TOR: '#00205B',
    UTA: '#69B3E7', VAN: '#00843D', VGK: '#B4975A', WSH: '#C8102E',
    WPG: '#004C97',
  };
  return colors[abbr] || '#00D4FF';
}
